/**
 * Pre-Consultation Chat Component
 * Interactive chat interface for medical pre-visit screening in Portuguese
 */

import React, { useState, useEffect, useRef } from 'react';
import './PreConsultationChat.css';

const PreConsultationChat = ({ onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Initialize chat with welcome message
  useEffect(() => {
    const initMessage = {
      id: 1,
      type: 'assistant',
      content: `Olá! Eu sou o MedGemma, seu assistente médico virtual. 🏥
      
Vou fazer algumas perguntas para entender melhor seus sintomas e preparar sua consulta médica.

Por favor, me conte: qual é o principal motivo da sua consulta hoje?`,
      timestamp: new Date().toISOString()
    };
    setMessages([initMessage]);
    setSessionId(Date.now().toString());
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatMessage = (content) => {
    // Convert line breaks to paragraphs for better formatting
    return content.split('\n').map((line, idx) => 
      line.trim() ? <p key={idx}>{line}</p> : <br key={idx} />
    );
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || conversationEnded) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Call MedGemma API directly
      const response = await fetch('https://medgemma-vllm.semdor.life/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'google/medgemma-4b-pt',
          messages: [
            {
              role: 'system',
              content: `Você é um assistente médico realizando uma pré-consulta em português brasileiro.
              Seu objetivo é coletar informações sobre os sintomas do paciente de forma empática e profissional.
              Faça uma pergunta por vez. Seja claro e objetivo.
              Foque em: início dos sintomas, intensidade, frequência, fatores desencadeantes, tratamentos já tentados, histórico médico relevante.
              Após coletar informações suficientes (cerca de 5-6 perguntas), ofereça um resumo e orientações preliminares.`
            },
            ...messages.map(msg => ({
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content
            })),
            {
              role: 'user',
              content: userMessage.content
            }
          ],
          temperature: 0.7,
          max_tokens: 400,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const assistantContent = data.choices[0].message.content;

      // Check if conversation should end (after collecting enough info)
      const shouldEnd = messages.length >= 10 && assistantContent.toLowerCase().includes('resumo');

      const assistantMessage = {
        id: messages.length + 2,
        type: 'assistant',
        content: assistantContent,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (shouldEnd) {
        setTimeout(() => {
          setConversationEnded(true);
        }, 2000);
      }

    } catch (error) {
      console.error('Error calling MedGemma:', error);
      
      const errorMessage = {
        id: messages.length + 2,
        type: 'assistant',
        content: `Desculpe, houve um problema ao processar sua mensagem. Por favor, tente novamente.
        
Se o problema persistir, verifique se o servidor MedGemma está em execução.`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const generateReport = () => {
    const report = messages
      .filter(msg => msg.type === 'user')
      .map((msg, idx) => `${idx + 1}. ${msg.content}`)
      .join('\n');

    const blob = new Blob([`RELATÓRIO DE PRÉ-CONSULTA\nData: ${new Date().toLocaleDateString('pt-BR')}\n\nRESPOSTAS DO PACIENTE:\n${report}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pre-consulta-${sessionId}.txt`;
    a.click();
  };

  const restartChat = () => {
    setMessages([{
      id: 1,
      type: 'assistant',
      content: `Olá! Eu sou o MedGemma, seu assistente médico virtual. 🏥
      
Vou fazer algumas perguntas para entender melhor seus sintomas e preparar sua consulta médica.

Por favor, me conte: qual é o principal motivo da sua consulta hoje?`,
      timestamp: new Date().toISOString()
    }]);
    setConversationEnded(false);
    setSessionId(Date.now().toString());
  };

  return (
    <div className="pre-consultation-chat">
      <div className="chat-header">
        <button className="back-button" onClick={onBack}>
          ← Voltar
        </button>
        <div className="header-content">
          <img src="/assets/medgemma.avif" alt="MedGemma" className="header-logo" />
          <div className="header-text">
            <h2>Pré-Consulta Interativa</h2>
            <p>Assistente médico virtual em português</p>
          </div>
        </div>
        {conversationEnded && (
          <button className="report-button" onClick={generateReport}>
            📄 Baixar Relatório
          </button>
        )}
      </div>

      <div className="chat-container" ref={chatContainerRef}>
        <div className="messages-list">
          {messages.map(message => (
            <div 
              key={message.id} 
              className={`message ${message.type} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-avatar">
                {message.type === 'user' ? (
                  <div className="user-avatar">👤</div>
                ) : (
                  <img src="/assets/ai_headshot.svg" alt="MedGemma" className="ai-avatar" />
                )}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-sender">
                    {message.type === 'user' ? 'Você' : 'MedGemma'}
                  </span>
                  <span className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString('pt-BR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <div className="message-text">
                  {formatMessage(message.content)}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message assistant loading">
              <div className="message-avatar">
                <img src="/assets/ai_headshot.svg" alt="MedGemma" className="ai-avatar" />
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-input-container">
        {conversationEnded ? (
          <div className="conversation-ended">
            <p>Consulta pré-agendamento concluída!</p>
            <div className="end-actions">
              <button onClick={generateReport} className="action-button primary">
                📄 Baixar Relatório
              </button>
              <button onClick={restartChat} className="action-button secondary">
                🔄 Nova Consulta
              </button>
            </div>
          </div>
        ) : (
          <div className="input-wrapper">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua resposta aqui..."
              disabled={isLoading}
              rows="2"
              className="chat-input"
            />
            <button 
              onClick={handleSendMessage} 
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? '⏳' : '➤'}
            </button>
          </div>
        )}
      </div>

      <div className="chat-disclaimer">
        <p>
          ⚠️ Este é um assistente de IA para fins educacionais. 
          Sempre consulte um profissional de saúde para diagnóstico e tratamento médico.
        </p>
      </div>
    </div>
  );
};

export default PreConsultationChat;
